import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Box from '@mui/material/Box';

// Icons - Import outlined versions for lighter appearance
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ExpandLessOutlined from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

// Maintenance submenu icons - outlined
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FolderSpecialOutlinedIcon from '@mui/icons-material/FolderSpecialOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

import globalStyles, { StyledTooltip } from '../common/globalStyles';

import { styled } from '@mui/material/styles';

import { useUser } from '../common/components/UserContext';

// Maintenance Menu with collapsible submenu
export const MaintenanceMenu = ({ drawerOpen }) => {
  const { canAccessMaintenance } = useUser();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  // Check if the current route is in the maintenance section and auto-expand
  // But only if the drawer is open
  React.useEffect(() => {
    if (location.pathname.startsWith('/maintenance') && drawerOpen) {
      setOpen(true);
    } else if (!drawerOpen) {
      setOpen(false); // Collapse submenu when drawer collapses
    }
  }, [location.pathname, drawerOpen]);

  // Style for submenu items - inherits from main menu but with left padding
  const submenuStyle = {
    ...globalStyles.mainMenu,
    pl: 4, // Add padding to indent submenu items
    fontSize: '13px', fontWeight: 'normal',
  };

  // Don't render at all if user can't access maintenance due to permissions
  if (!canAccessMaintenance()) {
    return null;
  }

  return (
    <React.Fragment>
        {/* Split into two components: display label and navigation button */}
        <Box sx={{ display: 'flex', width: '100%' }}>
          {/* Main menu item - most of the width, clicks navigate to dashboard */}
          <ListItemButton 
            component={Link} 
            to="/maintenance" 
            sx={{ 
              ...globalStyles.mainMenu,
              flexGrow: 1,
              width: 'calc(100% - 40px)' 
            }}
          >
            <StyledTooltip title="Maintenance" placement="right" arrow disableHoverListener={drawerOpen}>
              <ListItemIcon>
                <ConstructionOutlined sx={{ color: '#f0f0f0' }} />
              </ListItemIcon>
            </StyledTooltip>
            <ListItemText primary="Maintenance" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
          </ListItemButton>
          
          {/* Toggle button - just handles expand/collapse - only show when drawer is open */}
          {drawerOpen && (
            <ListItemButton
              onClick={() => setOpen(!open)}
              sx={{
                ...globalStyles.mainMenu,
                minWidth: '40px',
                width: '40px',
                p: 0,
                justifyContent: 'center'
              }}
            >
              {open ? <ExpandLessOutlined sx={{ color: '#f0f0f0' }} /> : <ExpandMoreOutlined sx={{ color: '#f0f0f0' }} />}
            </ListItemButton>
          )}
        </Box>
        
        <Collapse in={open && drawerOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ backgroundColor: '#1b1b1b' }}>
            {/* <ListItemButton component={Link} to="/maintenance" sx={submenuStyle}>
              <ListItemIcon>
                <DashboardOutlinedIcon sx={{ color: '#888', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton> */}
            
            <ListItemButton component={Link} to="/maintenance/locations" sx={submenuStyle}>
              <ListItemIcon>
                <PlaceOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Locations" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>

            <ListItemButton component={Link} to="/maintenance/requesters" sx={submenuStyle}>
              <ListItemIcon>
                <PersonOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Requesters" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>

            <ListItemButton component={Link} to="/maintenance/typeoflaw" sx={submenuStyle}>
              <ListItemIcon>
                <GavelOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Types of Law" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/usergroups" sx={submenuStyle}>
              <ListItemIcon>
                <GroupWorkOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="User Groups" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/offices" sx={submenuStyle}>
              <ListItemIcon>
                <BusinessOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Offices" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/media" sx={submenuStyle}>
              <ListItemIcon>
                <ArticleOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Media" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/filestatus" sx={submenuStyle}>
              <ListItemIcon>
                <FolderSpecialOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="File Status" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/events" sx={submenuStyle}>
              <ListItemIcon>
                <EventOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Events" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
            
            <ListItemButton component={Link} to="/maintenance/departments" sx={submenuStyle}>
              <ListItemIcon>
                <CategoryOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Departments" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>

            <ListItemButton component={Link} to="/maintenance/stdfoldersubfolder" sx={submenuStyle}>
              <ListItemIcon>
                <CategoryOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
              </ListItemIcon>
              <ListItemText primary="Standard Folders" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
            </ListItemButton>
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

// Retention Menu with collapsible submenu
export const RetentionMenu = ({ drawerOpen }) => {
  const { canAccessMaintenance } = useUser();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  // Check if the current route is in the retention section and auto-expand
  // But only if the drawer is open
  React.useEffect(() => {
    if (location.pathname.startsWith('/retention') && drawerOpen) {
      setOpen(true);
    } else if (!drawerOpen) {
      setOpen(false); // Collapse submenu when drawer collapses
    }
  }, [location.pathname, drawerOpen]);

  // Style for submenu items - inherits from main menu but with left padding
  const submenuStyle = {
    ...globalStyles.mainMenu,
    pl: 4, // Add padding to indent submenu items
    fontSize: '13px', fontWeight: 'normal',
  };

  // Don't render at all if user can't access retention due to user permissions
  if (!canAccessMaintenance()) {
    return null;
  }

  return (
    <React.Fragment>
      {/* Split into two components: display label and navigation button */}
      <Box sx={{ display: 'flex', width: '100%' }}>
        {/* Main menu item - most of the width, clicks navigate to review queue */}
        <ListItemButton 
          component={Link} 
          to="/retention/review" 
          sx={{ 
            ...globalStyles.mainMenu,
            flexGrow: 1,
            width: 'calc(100% - 40px)' 
          }}
        >
          <StyledTooltip title="Retention" placement="right" arrow disableHoverListener={drawerOpen}>
            <ListItemIcon>
              <ArchiveOutlinedIcon sx={{ color: '#f0f0f0' }} />
            </ListItemIcon>
          </StyledTooltip>
          <ListItemText primary="Retention" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
        </ListItemButton>
        
        {/* Toggle button - just handles expand/collapse - only show when drawer is open */}
        {drawerOpen && (
          <ListItemButton
            onClick={() => setOpen(!open)}
            sx={{
              ...globalStyles.mainMenu,
              minWidth: '40px',
              width: '40px',
              p: 0,
              justifyContent: 'center'
            }}
          >
            {open ? <ExpandLessOutlined sx={{ color: '#f0f0f0' }} /> : <ExpandMoreOutlined sx={{ color: '#f0f0f0' }} />}
          </ListItemButton>
        )}
      </Box>
      
      <Collapse in={open && drawerOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ backgroundColor: '#1b1b1b' }}>
          <ListItemButton component={Link} to="/retention/review" sx={submenuStyle}>
            <ListItemIcon>
              <AssignmentOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
            </ListItemIcon>
            <ListItemText primary="Review Queue" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
          </ListItemButton>

          <ListItemButton component={Link} to="/retention/policies" sx={submenuStyle}>
            <ListItemIcon>
              <ArticleOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
            </ListItemIcon>
            <ListItemText primary="Policies" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
          </ListItemButton>

          <ListItemButton component={Link} to="/retention/audit" sx={submenuStyle}>
            <ListItemIcon>
              <HistoryOutlinedIcon sx={{ color: '#f0f0f0', fontSize: '1.1rem' }} />
            </ListItemIcon>
            <ListItemText primary="Audit Trail" primaryTypographyProps={{ fontSize: '13px', fontWeight: 'normal' }} />
          </ListItemButton>
        </List>
      </Collapse>
    </React.Fragment>
  );
};

// Main list items don't change
export const MainListItems = ({ drawerOpen }) => {
  return (
    <React.Fragment>
      <ListItemButton component={Link} to="/" sx={globalStyles.mainMenu}>
        <StyledTooltip title="Dashboard" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <BarChartOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>

      <ListItemButton component={Link} to="/inventory" sx={globalStyles.mainMenu}>
        <StyledTooltip title="Inventory" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <InventoryOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="Inventory" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>

      <ListItemButton component={Link} to="/file-requests" sx={globalStyles.mainMenu}>
        <StyledTooltip title="File Requests" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <FolderSharedOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="File Requests" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>

      <RetentionMenu drawerOpen={drawerOpen} />
      
      <ListItemButton component={Link} to="/reports" sx={globalStyles.mainMenu}>
        <StyledTooltip title="Reports" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <DescriptionOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="Reports" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>
    </React.Fragment>
  );
};

// Secondary items now include the maintenance menu and remaining items
export const SecondaryListItems = ({ drawerOpen }) => {
  const { canAccessMaintenance, canAccessSettings, canAccessTransactionLog } = useUser();
  
  // Don't render at all if user can't access retention
  if (!canAccessMaintenance()) {
    return null;
  }

  return (
    <React.Fragment>
      {canAccessMaintenance() && <MaintenanceMenu drawerOpen={drawerOpen} />}

      <ListItemButton component={Link} to="/users" sx={globalStyles.mainMenu}>
        <StyledTooltip title="User Management" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <PeopleOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="User Management" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>

      <ListItemButton component={Link} to="/activityhistory" sx={globalStyles.mainMenu}>
        <StyledTooltip title="Transaction Log" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <TimelineOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="Transaction Log" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>

      <ListItemButton component={Link} to="/settings" sx={globalStyles.mainMenu}>
        <StyledTooltip title="Settings" placement="right" arrow disableHoverListener={drawerOpen}>
          <ListItemIcon>
            <SettingsOutlinedIcon sx={{ color: '#f0f0f0' }} />
          </ListItemIcon>
        </StyledTooltip>
        <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '14px', fontWeight: 'normal' }} />
      </ListItemButton>
    </React.Fragment>
  );
};